// src/screens/BuyCredits.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import { PrimaryButton, TertiaryButton } from "../components/Button";
import QuantitySelector from "../components/QuantitySelector";
import PageTitle from "../components/PageTitle";
import { createCheckoutSession, getUserProfile, bookSession, getCreditCost } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { formatTo24Hour } from "../utils/timeUtils";
import PaymentResultModal from "../components/PaymentResultModal";

/* small inline icon */
const Bolt = ({ className = "" }) => (
    <svg className={className} width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path
            d="M8 13H3L13 1h3l-3 8h6L12 23H9l3-10H8Z"
            stroke="#F4C505"
            strokeWidth="2"
            fill="none"
        />
    </svg>
);

export default function BuyCredits() {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { user } = useAuth();

    // UI state
    const [credits, setCredits] = useState(10);
    const [creditCost, setCreditCost] = useState(0);
    const totalCost = credits * creditCost;

    // Real user data
    const [userProfile, setUserProfile] = useState(null);
    const [remainingCredits, setRemainingCredits] = useState(0);
    const [loading, setLoading] = useState(true);

    // modal state
    const [result, setResult] = useState(null); // "success" | "failure" | null
    const [processing, setProcessing] = useState(false);
    const [paymentError, setPaymentError] = useState(null);
    const [booking, setBooking] = useState(false);

    const quickPicks = [20, 50, 100, 250];

    // Handle payment return from Stripe checkout
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        const userId = urlParams.get('user_id');
        const cancelled = urlParams.get('cancelled');

        if (sessionId && userId && user?.id === userId) {
            if (cancelled === 'true') {
                // Payment was cancelled
                setResult("failure");
                setPaymentError("Payment was cancelled. Please try again.");
            } else {
                // User returned from successful Stripe checkout
                handlePaymentReturn();
            }
        }
    }, [user?.id]);

    // Fetch user profile, credits, and credit cost
    useEffect(() => {
        const fetchData = async () => {
            if (!user?.id) return;

            try {
                setLoading(true);

                // Fetch user profile and credit cost in parallel
                const [profileResponse, creditCostResponse] = await Promise.all([
                    getUserProfile(user.id),
                    getCreditCost()
                ]);

                setUserProfile(profileResponse.user);
                setRemainingCredits(profileResponse.user.credits || 0);
                setCreditCost(creditCostResponse.creditCost || 10);
                console.log('User profile loaded:', profileResponse.user);
                console.log('Credit cost loaded:', creditCostResponse.creditCost);
            } catch (error) {
                console.error('Error fetching data:', error);
                setPaymentError('Failed to load data');
                setCreditCost(10); // fallback
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [user?.id]);

    const dec = () => setCredits((c) => Math.max(1, c - 1));
    const inc = () => setCredits((c) => c + 1);

    const handlePay = async () => {
        try {
            setProcessing(true);
            setPaymentError(null);

            // Create checkout session with backend
            const checkoutResponse = await createCheckoutSession(credits, user.id);
            console.log('Checkout response:', checkoutResponse);

            if (checkoutResponse.url) {
                // Redirect to Stripe checkout URL provided by backend
                console.log('Redirecting to Stripe checkout:', checkoutResponse.url);
                window.location.href = checkoutResponse.url;
            } else {
                console.error('Invalid checkout response:', checkoutResponse);
                throw new Error('Failed to create checkout session - no URL received');
            }
        } catch (error) {
            console.error('Error creating checkout session:', error);
            setPaymentError('Failed to initiate payment. Please try again.');
            setProcessing(false);
        }
    };

    // Handle successful payment return from Stripe checkout
    const handlePaymentReturn = async () => {
        if (!user?.id) return;

        try {
            setProcessing(true);
            const profileResponse = await getUserProfile(user.id);
            setRemainingCredits(profileResponse.user?.credits || 0);
            setResult("success");
        } catch (error) {
            console.error('Error refreshing profile after payment:', error);
            setResult("success");
        } finally {
            setProcessing(false);
        }
    };

    const handleBookAfterPayment = async () => {
        if (!state?.trainer || !state?.time || !state?.day) {
            console.error("Missing booking data from navigation state");
            navigate("/student/home");
            return;
        }

        try {
            setBooking(true);

            // Parse the time slot and create booking data
            const [startTimeSlot, endTimeSlot] = state.time.split(' - ');
            const baseDate = state.day.fullDate; // YYYY-MM-DD format

            // Convert time to 24-hour format for API
            const startTime24 = formatTo24Hour(startTimeSlot) + ':00';
            const endTime24 = formatTo24Hour(endTimeSlot) + ':00';

            // Get user's timezone
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            const bookingData = {
                trainerId: state.trainer._id,
                userId: user._id || user.id,
                startDate: baseDate,
                startTime: startTime24,
                endTime: endTime24,
                timezone: userTimezone,
            };

            console.log('Booking session after payment with data:', bookingData);
            const result = await bookSession(bookingData);

            // Navigate to success screen
            navigate('/student/home', {
                state: {
                    bookingSuccess: true,
                    bookingDetails: {
                        date: state.day.date,
                        time: state.time,
                        trainerName: state.trainer.name,
                        location: state.selectedAddress || "HOME",
                        address: "Your preferred location"
                    }
                }
            });

        } catch (error) {
            console.error('Error booking session after payment:', error);
            alert(error.response?.data?.message || 'Failed to book session. Please try again.');
        } finally {
            setBooking(false);
        }
    };

    return (
        <div className="h-[100dvh] w-full bg-[#F7F7F7] overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>
            <div className="w-full min-h-full flex flex-col">
                {/* NAVBAR */}
                <Navbar
                    onBack={() => navigate("/student/home", { state: { refreshCredits: true } })}
                    background="transparent"
                    spacerHeight={40}
                />

                {/* CONTENT */}
                <main className="flex-1 px-5">
                    <div className="pt-4">
                        {/* TITLE */}
                        <PageTitle>Buy Credits</PageTitle>


                        {/* QUANTITY SELECTOR */}
                        <div className="mt-6">
                            <QuantitySelector
                                value={credits}
                                onIncrement={inc}
                                onDecrement={dec}
                                min={1}
                                disabled={processing || loading}
                            />
                        </div>

                        {/* quick picks */}
                        <div className="mt-4 grid grid-cols-4 gap-4">
                            {quickPicks.map((v) => (
                                <button
                                    key={v}
                                    onClick={() => setCredits(v)}
                                    className={`h-[40px] bg-white border ${credits === v ? "border-black" : "border-[#DEDEDE]"
                                        } grid place-items-center text-[14px]`}
                                >
                                    {v}
                                </button>
                            ))}
                        </div>

                        {/* price banner */}
                        <div className="mt-5 bg-[#FFF6D9] border border-[#F5E6AB] px-4 py-3 flex items-center justify-between">
                            <Bolt />
                            <div className="text-[14px]">
                                <span className="text-[#616161] mr-2">1 Credit</span>
                                <span className="font-extrabold">${creditCost}</span>
                            </div>
                            <Bolt />
                        </div>
                    </div>
                </main>

                {/* Sticky footer constrained to the shell */}
                <footer className="sticky bottom-0 z-40 bg-white border-t">
                    <div className="px-5 py-3 pb-[max(12px,env(safe-area-inset-bottom))]">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-[#8C8C8C]">Total cost</span>
                            <span className="font-extrabold text-[16px]">${totalCost}</span>
                        </div>
                        <PrimaryButton
                            label={processing ? "PROCESSING..." : "PAY WITH STRIPE"}
                            onClick={handlePay}
                            disabled={processing || loading}
                            className="!w-full"
                        />
                    </div>
                </footer>
            </div>


            {/* Result modal */}
            <PaymentResultModal
                open={result !== null}
                type={result || "success"}
                remainingCredits={remainingCredits}
                onClose={() => setResult(null)}
                onRetry={() => {
                    setResult(null);
                    handlePay();
                }}
                onBook={() => {
                    setResult(null);
                    if (state?.trainer) {
                        // If we have booking data, complete the booking
                        handleBookAfterPayment();
                    } else {
                        // Otherwise, go to explore trainers
                        navigate("/explore-trainers");
                    }
                }}
            />
        </div>
    );
}
